import { layerAlert, layerConfirm } from '../../components/LayerPopup/LayerPopup';
import { deleteTeam, type TeamDto } from '../../api/project-teams';
import { tProject } from '../../lib/project-i18n';

/**
 * 스페이스(팀) 삭제 공통 플로우 (2026-08-31) — confirmDeleteProject 와 같은 모양.
 *
 * 프로젝트 홈의 스페이스 카드는 **진행 중 프로젝트가 있는 스페이스만** 뜬다(space-cards.ts).
 * 그래서 서버 기본 정책(활성 프로젝트 있으면 409)만으로는 이 카드에서 삭제가 늘 막힌다.
 * 대신 함께 지워질 프로젝트 수·이름을 확인창에 먼저 보여 주고, 사용자가 동의했을 때만
 * `cascade=true` 로 프로젝트까지 한 트랜잭션에서 지운다.
 *
 * 권한은 서버가 SSOT (LEADER only) — 403 은 안내 알림으로 처리한다.
 */

/** 삭제 진입점을 띄울지 — 리더(또는 관리자 우회)만. 서버 권한의 UI 미러. */
export function canDeleteSpace(team: Pick<TeamDto, 'myRole'>): boolean {
  return team.myRole === 'LEADER' || team.myRole === 'ADMIN_BYPASS';
}

function errCode(err: unknown): string | undefined {
  return (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
}

export interface DeleteSpaceTarget {
  id: string;
  name: string;
  /** 함께 지워질 진행 중 프로젝트 이름들 — 확인창 미리보기에 쓴다. */
  projectNames: string[];
  /** 진행 중 프로젝트 수. 목록보다 클 수 있다(구버전 서버 폴백 시 집계값). */
  projectCount: number;
}

/** 확인 후 삭제. 실제로 지워졌으면 true. */
export async function confirmAndDeleteSpace(target: DeleteSpaceTarget): Promise<boolean> {
  const n = Math.max(target.projectCount, target.projectNames.length);
  let detail: string;
  if (n > 0) {
    let names = target.projectNames.slice(0, 3).join(', ');
    if (n > 3) names += tProject('project.delete.more_suffix', { n: n - 3 });
    detail = tProject('project.space.delete.cascade_warn', { n, names: names || '…' });
  } else {
    detail = tProject('project.space.delete.projects_none');
  }

  const msg = [
    tProject('project.space.delete.confirm', { name: target.name }),
    detail,
    tProject('project.space.delete.irreversible'),
  ].join('\n');
  if (!(await layerConfirm(msg, { danger: true }))) return false;

  try {
    // 프로젝트가 있다고 본 경우에만 cascade — 빈 스페이스는 종전 경로 그대로 간다.
    /*
     * ⛔⛔ 2026-10-10 — ★ 정본의 이 줄은 ⭐ `deleteTeam(target.id, { cascade: n > 0 })` 이다.
     *   ⇒ ★ 「정본 그대로 이관」에서 ⭐ «이 한 줄만» 뺐다 — ⇒ 관제 지시 Ⓐ(cascade 는 «나중에»).
     * ⇒ ⭐ 까닭이 ★ 둘이다(둘 다 실측):
     *   ① 우리 `api/project-teams.ts` 의 `deleteTeam` 은 ★ 인자가 ⭐ «하나»다
     *      정본 ⇒ deleteTeam(id, opts?: { cascade?: boolean })  ⇒ ?cascade=true 를 붙인다
     *      우리 ⇒ deleteTeam(id)                                ⇒ ⇒ 그래서 TS2554 가 났다
     *   ② ⛔ 그리고 ★ 우리 «서버»가 ⭐ cascade 를 ★ 0 건 안다
     *      apps/chat-server/.../project-teams.routes.ts ⇒ 정본 15 ⇔ 우리 ★ 0
     *      ⇒ ⇒ ★ 즉 넓히고 보내도 ⇒ ⭐ 서버가 «조용히 무시»한다 ⇒ ⇒ 화면만 거짓말을 한다
     * ⇒ ⚠ 그리고 ★ 그것은 ⭐ «사용자 결정»이다 — ⇒ 결정 문서 ❶-① 「스페이스를 지울 때
     *   프로젝트를 함께 지우지 «않는다» — 활성 프로젝트가 있으면 409」.
     * ⏳ 만료 조건 — ★ «이름»으로
     *   ⇒ ① 우리 `api/project-teams` 의 `deleteTeam` 이 ★ 둘째 인자를 ⭐ 받고
     *   ⇒ ② 서버 project-teams 라우트가 ★ `cascade` «라는 이름»을 ⭐ 아는 날
     *      ⇒ ⇒ ★ 그때 ⭐ 위 정본 줄로 되돌려라(이 주석을 지우고).
     *   ⇒ ⛔ 「그 파일이 있다」로 재지 마라 — ★ 둘 다 ⭐ 지금도 «있다». 가르는 것은 ★ 서명과 칸이다.
     */
    await deleteTeam(target.id);
    return true;
  } catch (err) {
    const code = errCode(err);
    void layerAlert(
      code === 'FORBIDDEN'
        ? tProject('project.space.delete.err_forbidden')
        : code === 'TEAM_HAS_ACTIVE_PROJECTS'
          ? tProject('project.team.delete.err_active')
          : tProject('project.space.delete.err'),
    );
    return false;
  }
}
